import React, { useMemo, useCallback, useState } from 'react';
import { isFunction, get, isEmpty } from 'lodash';
import { arrayOf, element, string, func, oneOfType, object } from 'prop-types';
import Step from './Step';
import StepperContext from './StepperContext';

const DEFAULT_STEP_INDEX = 0;

/**
 * @deprecated Please use useStepper hook from @fiverr-private/navigation instead.
 */
const Stepper = ({ children, initialStepId, onComplete, steps }) => {
    const findIndexById = useCallback(((id) => steps.findIndex((step) => step.id === id)), [steps]);

    const initialIndex = useMemo(() => {
        const index = findIndexById(initialStepId);

        return index >= 0 ? index : DEFAULT_STEP_INDEX;
    }, [findIndexById, initialStepId]);

    const [currentStepIndex, setCurrentStepIndex] = useState(initialIndex);
    const [isNextDisabled, setIsNextDisabled] = useState(false);
    const [isBackEnabled, setIsBackEnabled] = useState(true);
    const [isNextLoading, setIsNextLoading] = useState(false);
    const [isNavigateLoading, setIsNavigateLoading] = useState(false);
    const currentStepId = get(steps[currentStepIndex], 'id', initialStepId);
    const isLastStep = currentStepIndex === steps.length - 1;
    const isFirstStep = currentStepIndex === 0;

    const navigateById = useCallback(async(id, onNavigate = Function.prototype) => {
        setIsNavigateLoading(true);

        try {
            await onNavigate();

            setCurrentStepIndex(findIndexById(id));
        } catch (error) {
            // Swallow the error and prevent the action
        } finally {
            setIsNavigateLoading(false);
        }
    }, [findIndexById]);

    const reset = useCallback(() => {
        setCurrentStepIndex(0);
    }, []);

    const next = useCallback(async(onNext = Function.prototype) => {
        if (isNextDisabled) {return;}

        setIsNextLoading(true);

        try {
            await onNext();

            if (isLastStep) {
                await onComplete();
            } else {
                setCurrentStepIndex((index) => index + 1);
            }
        } catch (error) {
            // Swallow the error and prevent the action
        } finally {
            setIsNextLoading(false);
        }
    }, [isLastStep, isNextDisabled, onComplete]);

    const back = useCallback(() => {
        if (!isFirstStep) {
            setCurrentStepIndex(currentStepIndex - 1);
        }
    }, [currentStepIndex, isFirstStep]);

    const context = useMemo(() => ({
        currentStepIndex,
        currentStepId,
        initialStepId,
        isNextLoading,
        isNavigateLoading,
        isBackEnabled,
        isNextDisabled,
        isLastStep,
        isFirstStep,
        navigateByIndex: setCurrentStepIndex,
        navigateById,
        steps,
        enableNext: () => setIsNextDisabled(false),
        disableNext: () => setIsNextDisabled(true),
        enableBack: () => setIsBackEnabled(true),
        disableBack: () => setIsBackEnabled(false),
        next,
        back,
        reset
    }), [
        back,
        currentStepId,
        initialStepId,
        currentStepIndex,
        isBackEnabled,
        isFirstStep,
        isLastStep,
        isNavigateLoading,
        isNextDisabled,
        isNextLoading,
        navigateById,
        next,
        steps,
        reset
    ]);

    if (isEmpty(steps)) {return null;}

    return (
        <StepperContext.Provider value={context}>
            {isFunction(children) ? children(context) : children}
        </StepperContext.Provider>
    );
};

const useStepperContext =  () => React.useContext(StepperContext);
Stepper.useContext = useStepperContext;
Stepper.Step = Step;

Stepper.displayName = 'Stepper';

Stepper.propTypes = {
    children: oneOfType([arrayOf(element), func]).isRequired,
    initialStepId: string,
    onComplete: func,
    steps: arrayOf(object).isRequired
};

Stepper.defaultProps = {
    onComplete: Function.prototype,
    initialStepId: '',
    steps: []
};

export default Stepper;
